import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import axios from 'axios';

const CVSectionDialog = ({ open, onClose, section, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order_index: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (section) {
      setFormData({
        title: section.title || '',
        description: section.description || '',
        order_index: section.order_index || 0,
        is_active: section.is_active !== undefined ? section.is_active : true
      });
    } else {
      setFormData({
        title: '',
        description: '',
        order_index: 0,
        is_active: true
      });
    }
    setError('');
  }, [section, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (section) {
        // Update existing section
        await axios.put(`/api/cv/admin/sections/${section.id}`, formData);
      } else {
        // Create new section
        await axios.post('/api/cv/admin/sections', formData);
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving section:', error);
      setError(error.response?.data?.message || 'Failed to save section');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {section ? 'Edit CV Section' : 'Add New CV Section'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Section Title"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.title}
            onChange={handleInputChange('title')}
            required
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange('description')}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Order Index"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.order_index}
            onChange={handleInputChange('order_index')}
            disabled={loading}
            sx={{ mb: 2 }}
            helperText="Lower numbers appear first"
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleInputChange('is_active')}
                disabled={loading}
              />
            }
            label="Active (visible on CV)"
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Saving...' : (section ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CVSectionDialog;